<template>
  <NodeViewWrapper as="span" class="relative">
    <Popover 
      :open="isPopoverOpen"
      @update:open="onPopoverOpenChange"
      :modal="false"
    >
      <PopoverTrigger
        as="span"
        @mousedown.stop
        @click.stop
      >
        <button
          ref="triggerRef"
          class="bg-editor-resourceBackground border-b-2 border-editor-resourceBorder text-blue-400 p-0.5 cursor-pointer hover:text-blue-500 hover:border-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
          aria-haspopup="dialog"
          aria-controls="resource-preview-popover"
        >
          <span class="material-icons-outlined text-xl relative top-[3px] mr-0.5">file_download</span>
          <span class="align-baseline">{{ node.attrs.label }}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref="popoverContentRef"
        id="resource-preview-popover"
        side="right"
        align="center"
        class="relative z-50 bg-white text-black border border-gray-300 rounded-md shadow-lg w-80 p-0 pointer-events-auto"
        @pointerDownOutside="onInteractOutside"
        @focusOutside="onInteractOutside"
        @interactOutside="onInteractOutside"
      >
        <PopoverArrow class="fill-white stroke-gray-300" :width="20" :height="12" />
        <ResourcePopoverPreview 
          v-if="resource" 
          :resource="resource"
          @close="handlePanelClose"
          @showResourcePreviewModal="openPreviewModal"
          @showSaveToFolderModal="openSaveModal"
        />
      </PopoverContent>
    </Popover>
  </NodeViewWrapper>
  <ResourcePreviewModal
    v-if="isPreviewModalVisible && resource"
    :uuid="resource.uuid"
    :title="resource.title"
    :type="resource.type"
    :download-links="resource.download_links"
    :google-drive-links="resource.google_drive_links"
    :screenshots="resource.screenshots"
    :num-pages="resource.num_pages"
    :language="resource.language"
    @update:visible="handlePreviewModalVisibility"
  />
  <SaveToFolderModal
    v-if="isSaveModalVisible && resource"
    :resource="resource"
    @update:visible="(value) => isSaveModalVisible = value"
    @resourceSaved="handleResourceSavedInModal"
    :contentUuid="resource.uuid"
    content-type="resource"
    content-type-label="Resource"
  />
</template>

<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { nodeViewProps, NodeViewWrapper } from '@tiptap/vue-3'
import ResourcePreviewModal from '@/resources/ResourcePreviewModal.vue'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import { PopoverArrow } from 'reka-ui'
import Popover from '../popover/Popover.vue'
import PopoverContent from '../popover/PopoverContent.vue'
import PopoverTrigger from '../popover/PopoverTrigger.vue'
import ResourcePopoverPreview from '@/resources/ResourcePopoverPreview.vue'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import { Resource } from '@/resources/resource_types'

const props = defineProps({
  ...nodeViewProps
})

const presentLevelsStore = usePresentLevelsStore()
const resource = computed<Resource | null>(() => {
  const resourceUUID = props.node.attrs.resourceUUID
  return resourceUUID ? presentLevelsStore.resourcesByUUID[resourceUUID] as Resource : null
})

const isPopoverOpen = ref(false)
const keepPopoverOpen = ref(false)
const triggerRef = ref<HTMLButtonElement | null>(null)
const isPreviewModalVisible = ref(false)
const isSaveModalVisible = ref(false)
const popoverContentRef = ref<HTMLDivElement | null>(null)

const handlePanelClose = () => {
  keepPopoverOpen.value = false
  isPopoverOpen.value = false
  nextTick(() => {
    triggerRef.value?.focus()
  })
}

const openSaveModal = () => {
  isSaveModalVisible.value = true
};

const handleResourceSavedInModal = () => {
  isSaveModalVisible.value = false
};

const openPreviewModal = () => {
  // Keep the popover open while showing the preview modal
  keepPopoverOpen.value = true
  isPopoverOpen.value = true
  isPreviewModalVisible.value = true
};

// Ensure the popover remains (or reopens) after closing the preview modal
const handlePreviewModalVisibility = (value: boolean) => {
  isPreviewModalVisible.value = value
  if (!value) {
    isPopoverOpen.value = true
    nextTick(() => {
      keepPopoverOpen.value = false
    })
  }
}

// Only allow the popover to close if we're not in the middle of preview interactions
const onPopoverOpenChange = (nextOpen: boolean) => {
  if (!nextOpen && (isPreviewModalVisible.value || keepPopoverOpen.value)) {
    isPopoverOpen.value = true
    return
  }
  isPopoverOpen.value = nextOpen
}

// Cancel outside interactions while the preview lifecycle is active,
// so the popover doesn't auto-close when focus moves to the modal/back.
const onInteractOutside = (event: Event) => {
  if (isPreviewModalVisible.value || keepPopoverOpen.value) {
    event.preventDefault?.()
  }
}
</script>
